import React from 'react';
import { Box, TextField, Button, CircularProgress, Typography, Paper } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface ChatInputBarProps {
    newMessage: string;
    setNewMessage: (message: string) => void;
    isLoading: boolean;
    threadId: string | null;
    isInitializingThread: boolean;
    isMobile: boolean;
    onSendMessage: (e: React.FormEvent) => void;
}

const ChatInputBar: React.FC<ChatInputBarProps> = ({
    newMessage,
    setNewMessage,
    isLoading,
    threadId,
    isInitializingThread,
    isMobile,
    onSendMessage
}) => {
    const { t } = useTranslation();

    return (
        <Paper
            elevation={0}
            sx={{
                p: isMobile ? 1.5 : 2,
                bgcolor: 'var(--petsnap-canvas)',
                borderTop: '1px solid rgba(244, 162, 97, 0.15)',
                borderRadius: 0
            }}
        >
            {isInitializingThread ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} sx={{ color: 'var(--petsnap-honey)' }} />
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                        {t('initializingConversation')}
                    </Typography>
                </Box>
            ) : (
                <form onSubmit={onSendMessage}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                        <TextField
                            fullWidth
                            multiline
                            maxRows={isMobile ? 3 : 4}
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder={t('typeMessage')}
                            disabled={isLoading || !threadId}
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    fontSize: isMobile ? '16px' : '15px',
                                    color: 'var(--text-primary)',
                                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                                    '& fieldset': { borderColor: 'rgba(38, 70, 83, 0.1)' },
                                    '&:hover fieldset': { borderColor: 'rgba(244, 162, 97, 0.5)' },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'var(--petsnap-honey)',
                                        borderWidth: 2
                                    },
                                },
                            }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isLoading || !newMessage.trim() || !threadId}
                            sx={{
                                minWidth: isMobile ? 48 : 54,
                                height: isMobile ? 48 : 54,
                                borderRadius: 3,
                                background: 'var(--gradient-golden)',
                                boxShadow: '0 4px 12px rgba(244, 162, 97, 0.3)',
                                '&:hover': {
                                    filter: 'brightness(1.05)',
                                    boxShadow: '0 6px 16px rgba(244, 162, 97, 0.4)',
                                },
                                '&.Mui-disabled': {
                                    background: 'var(--petsnap-surface-elevated)',
                                    color: 'var(--text-tertiary)'
                                }
                            }}
                        >
                            {isLoading ? <CircularProgress size={24} sx={{ color: 'var(--petsnap-forest)' }} /> : <SendIcon sx={{ color: 'white' }} />}
                        </Button>
                    </Box>
                </form>
            )}
        </Paper>
    );
};

export default ChatInputBar;
